import React, { useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { Text } from '../components/Texte';
import { SafeAreaView } from 'react-native-safe-area-context';
import { GradientButton } from '../components/GradientButton';
import { BrandLogo } from '../components/BrandLogo';
import { DecorBlob } from '../components/DecorBlob';
import { updateProfile } from '../services/profile';
import { maskBirthDate, parseBirthDate } from '../utils/birthDate';
import { colonneFormulaire, colors, radius, screenPadding, spacing, typography } from '../theme/tokens';
import { t } from '../i18n';

export interface ProfileCompletionScreenProps {
  onComplete: () => void; // date enregistrée → on entre dans l'app
  onSkip: () => void; // « Plus tard » → on entre dans l'app sans date (redemandé au prochain lancement)
}

export function ProfileCompletionScreen({ onComplete, onSkip }: ProfileCompletionScreenProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setError(null);
    const result = parseBirthDate(value);
    if ('error' in result) {
      setError(result.error);
      return;
    }
    setSaving(true);
    try {
      await updateProfile({ birth_date: result.iso });
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('profileCompletion.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <DecorBlob variant="paywall" />
      <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <View style={[styles.content, colonneFormulaire]}>
          <View style={styles.logoWrap}>
            <BrandLogo size={44} testID="completion-logo" />
          </View>

          <Text style={styles.title}>{t('profileCompletion.title')}</Text>
          <Text style={styles.subtitle}>
            Ta date de naissance nous aide à mieux te connaître. C’est facultatif.
          </Text>

          <TextInput
            testID="completion-input"
            style={styles.input}
            placeholder={t('profileCompletion.birthDatePlaceholder')}
            placeholderTextColor={colors.grey400}
            keyboardType="number-pad"
            value={value}
            onChangeText={(v) => setValue(maskBirthDate(v))}
            maxLength={10}
          />

          {error ? (
            <Text testID="completion-error" style={styles.error}>
              {error}
            </Text>
          ) : null}

          <GradientButton
            label={t('profileCompletion.continue')}
            onPress={submit}
            loading={saving}
            withArrow
            testID="completion-submit"
          />

          <Pressable testID="completion-skip" onPress={onSkip} style={styles.skip} accessibilityRole="button">
            <Text style={styles.skipText}>{t('profileCompletion.later')}</Text>
          </Pressable>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  flex: { flex: 1 },
  content: { flex: 1, justifyContent: 'center', padding: screenPadding, gap: spacing.md },
  logoWrap: { alignSelf: 'center', marginBottom: spacing.md },
  title: { fontSize: typography.display.size, fontWeight: typography.display.weight, color: colors.night, textAlign: 'center' },
  subtitle: {
    fontSize: typography.body.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey500,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  input: {
    minHeight: 52,
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    fontSize: typography.h3.size,
    color: colors.grey900,
    backgroundColor: colors.white,
    textAlign: 'center',
    letterSpacing: 2,
  },
  error: { color: colors.error, fontSize: typography.small.size, textAlign: 'center' },
  skip: { alignSelf: 'center', padding: spacing.sm },
  skipText: { fontSize: typography.body.size, color: colors.grey500, fontWeight: '600' },
});
